import React, { useCallback, useEffect, useState } from 'react';
import axiosInstance from '../config/axios';
import { Search, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import './AuditLog.css';

const ENTITY_OPTIONS = [
  { value: '', label: 'All areas' },
  { value: 'user', label: 'Users' },
  { value: 'site', label: 'Sites' },
  { value: 'inventory_item', label: 'Inventory' }
];

const ACTION_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'created', label: 'Created' },
  { value: 'updated', label: 'Updated' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'import_created', label: 'Import Created' },
  { value: 'import_updated', label: 'Import Updated' }
];

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function formatTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${min}`;
}

function formatAction(action) {
  const map = {
    created: 'Created',
    updated: 'Updated',
    deleted: 'Deleted',
    deactivated: 'Deactivated',
    archived: 'Archived',
    import_created: 'Import Created',
    import_updated: 'Import Updated'
  };
  return map[action] || action || '—';
}

function formatEntityType(type) {
  const map = {
    user: 'Users',
    site: 'Sites',
    inventory_item: 'Inventory'
  };
  return map[type] || type || '—';
}

function formatDetails(details) {
  if (details == null || details === '') return '—';
  if (typeof details === 'string') {
    try {
      return formatDetails(JSON.parse(details));
    } catch (_) {
      return details;
    }
  }
  if (details.bulkClearByBrand) {
    return `Cleared ${details.deletedCount ?? 0} item(s) for brand ${details.brandName || details.brandId || ''}`;
  }
  if (details.importJobId || details.importType) {
    const parts = [];
    if (details.filename) parts.push(details.filename);
    if (details.created != null) parts.push(`${details.created} created`);
    if (details.updated != null) parts.push(`${details.updated} updated`);
    if (details.skips) parts.push(`${details.skips} skipped`);
    if (details.errors) parts.push(`${details.errors} errors`);
    return parts.join(' · ') || 'Import';
  }
  if (details.passwordChanged) {
    const changeKeys = details.changes ? Object.keys(details.changes) : [];
    const base = changeKeys.length ? `Changed: ${changeKeys.join(', ')}` : 'Updated';
    return `${base}; password changed`;
  }
  if (details.passwordSet) {
    return `Created (${details.role || 'user'}); password set`;
  }
  if (details.changes && typeof details.changes === 'object') {
    const keys = Object.keys(details.changes);
    if (!keys.length) return 'No field changes';
    return keys
      .map((k) => {
        const c = details.changes[k];
        const from = c && c.from != null ? String(c.from) : '—';
        const to = c && c.to != null ? String(c.to) : '—';
        return `${k}: ${from} → ${to}`;
      })
      .join('; ');
  }
  try {
    return JSON.stringify(details);
  } catch (_) {
    return String(details);
  }
}

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit };
      if (entityType) params.entityType = entityType;
      if (action) params.action = action;
      if (search) params.search = search;
      if (dateFrom) params.dateFrom = new Date(dateFrom + 'T00:00:00').toISOString();
      if (dateTo) params.dateTo = new Date(dateTo + 'T23:59:59.999').toISOString();
      const res = await axiosInstance.get('/api/tenant/audit-logs', { params });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setLogs([]);
      setTotal(0);
      setError(err.response?.data?.error || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, limit, entityType, action, search, dateFrom, dateTo]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const applySearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  return (
    <div className="audit-log">
      <div className="audit-section-header">
        <div>
          <h2 className="audit-section-title">Audit</h2>
          <p className="audit-section-subtitle">Track key changes to users, sites, and inventory</p>
        </div>
        <button type="button" className="btn btn-secondary audit-refresh-btn" onClick={loadLogs} disabled={loading}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      <div className="audit-filters">
        <select
          className="audit-filter-select"
          value={entityType}
          onChange={(e) => {
            setPage(1);
            setEntityType(e.target.value);
          }}
          aria-label="Filter by area"
        >
          {ENTITY_OPTIONS.map((o) => (
            <option key={o.value || 'all-entity'} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          className="audit-filter-select"
          value={action}
          onChange={(e) => {
            setPage(1);
            setAction(e.target.value);
          }}
          aria-label="Filter by action"
        >
          {ACTION_OPTIONS.map((o) => (
            <option key={o.value || 'all-action'} value={o.value}>{o.label}</option>
          ))}
        </select>
        <input
          type="date"
          className="audit-filter-date"
          value={dateFrom}
          onChange={(e) => {
            setPage(1);
            setDateFrom(e.target.value);
          }}
          aria-label="From date"
        />
        <input
          type="date"
          className="audit-filter-date"
          value={dateTo}
          onChange={(e) => {
            setPage(1);
            setDateTo(e.target.value);
          }}
          aria-label="To date"
        />
        <form className="audit-search-form" onSubmit={applySearch}>
          <div className="audit-search-wrap">
            <Search size={16} className="audit-search-icon" />
            <input
              type="search"
              className="audit-search-input"
              placeholder="Search user, item, details…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search audit logs"
            />
          </div>
          <button type="submit" className="btn btn-secondary audit-search-btn">Search</button>
        </form>
      </div>

      {error && <div className="audit-error">{error}</div>}

      <div className="table-container audit-table-wrap">
        <table className="table audit-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Time</th>
              <th>User</th>
              <th>Action</th>
              <th>Area</th>
              <th>Item</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="audit-empty">Loading…</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={7} className="audit-empty">No audit entries found</td></tr>
            ) : (
              logs.map((row) => (
                <tr key={row.id}>
                  <td>{formatDate(row.createdAt)}</td>
                  <td>{formatTime(row.createdAt)}</td>
                  <td>
                    <div className="audit-actor">
                      <span>{row.actorName || '—'}</span>
                      {row.actorEmail ? <span className="audit-actor-email">{row.actorEmail}</span> : null}
                    </div>
                  </td>
                  <td>{formatAction(row.action)}</td>
                  <td>{formatEntityType(row.entityType)}</td>
                  <td>{row.entityName || row.entityId || '—'}</td>
                  <td className="audit-details-cell">{formatDetails(row.details)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="audit-pagination">
        <span className="audit-pagination-meta">
          {total === 0 ? '0 entries' : `Page ${page} of ${totalPages} · ${total} total`}
        </span>
        <div className="audit-pagination-btns">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft size={16} /> Prev
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditLog;
